import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  ClerkProvider,
  useAuth as useClerkAuth,
  useOrganizationList,
  useUser,
} from '@clerk/nextjs'
import { useRouter } from 'next/router'

import config from '../config'
import useCookie from '../hooks/useCookie'
import { Role } from '../modules/adminUsers/roles'

const CLERK_SIGN_IN_URL = '/sign-in'

export const LS_KEY = 'bp-auth'
export const LS_WORKER_KEY = 'bp-worker-auth'

type BuildPassAuthUser = {
  externalId: string
  firstName: string
  lastName: string
  email: string
}

type Organisation = {
  id: string
  name: string
  imageUrl?: string
}

type BuildPassAuthContext = {
  token: string
  orgId: string
  isLoaded: boolean
  isSignedIn: boolean
  user: BuildPassAuthUser
  roles: Role[]
  organisations: Organisation[]
  login: () => void
  logout: () => void
  changeOrg: (newOrg: string, redirectUri?: string) => void
  authenticationError: () => void
  getNewToken: () => Promise<string>
}

const BuildPassAuthContext = createContext<BuildPassAuthContext>({
  token: undefined,
  orgId: undefined,
  isLoaded: false,
  isSignedIn: false,
  user: undefined,
  organisations: [],
  roles: [],
  login: () => {},
  logout: () => {},
  changeOrg: () => {},
  authenticationError: () => {},
  getNewToken: async () => null,
})

const mapClerkRolesToBp = (role: string) => {
  switch (role) {
    case 'org:bp_super':
      return Role.SuperAdmin
    case 'org:bp_sales':
      return Role.BpSales
    case 'org:builder_accounts':
      return Role.BuilderAccounts
    case 'org:builder_view_only':
      return Role.BuilderViewOnly
    case 'org:builder_owner':
      return Role.BuilderOwner
    case 'org:builder_manager':
      return Role.BuilderManager
    case 'org:builder_project_manager':
      return Role.BuilderProjectManager
    case 'org:worker':
      return Role.Worker
    case 'org:builder_custom':
      return Role.Custom
  }
}

const ClerkAuthProvider = ({ children }) => {
  const { user } = useUser()
  const { push } = useRouter()

  const { isLoaded, isSignedIn, signOut, getToken, orgId, userId, orgRole } =
    useClerkAuth()

  const {
    userMemberships: { isLoading: orgsLoading, data: orgsData },
    setActive,
    isLoaded: orgsLoaded,
  } = useOrganizationList({
    userMemberships: { infinite: true },
  })

  const [token, setToken] = useState<string>()

  const login = useCallback(() => {
    push(CLERK_SIGN_IN_URL)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isSignedIn && isLoaded && !token) {
      getToken({ template: 'Standard' }).then(setToken)
    } else if (isLoaded && !isSignedIn) {
      login()
    }
  }, [isSignedIn, isLoaded, getToken, token, push, login, orgId])

  const logout = useCallback(() => {
    signOut()
    login()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const authenticationError = useCallback(() => {
    push('/500')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const changeOrg = useCallback(
    async (newOrg: string, redirectUri?: string) => {
      if (decodeURIComponent(redirectUri) === '/select-organisation') {
        redirectUri = undefined
      }

      await setActive({ organization: newOrg })
      global.location.href = redirectUri ?? config.auth.redirectPath
    },

    [setActive]
  )

  const getNewToken = useCallback(async () => {
    return getToken({ template: 'Standard' })
  }, [getToken])

  const organisations = useMemo<Organisation[]>(() => {
    return orgsData?.map((mem) => ({
      id: mem.organization.id,
      name: mem.organization.name,
      imageUrl: mem.organization.imageUrl,
    }))
  }, [orgsData])

  return (
    <BuildPassAuthContext.Provider
      value={{
        token,
        orgId,
        isLoaded: isLoaded && orgsLoaded && !orgsLoading,
        isSignedIn,
        user: {
          externalId: userId,
          firstName: user?.firstName,
          lastName: user?.lastName,
          email: user?.primaryEmailAddress?.emailAddress,
        },
        roles: [mapClerkRolesToBp(orgRole)],
        organisations,
        login,
        logout,
        changeOrg,
        authenticationError,
        getNewToken,
      }}
    >
      {children}
    </BuildPassAuthContext.Provider>
  )
}

export const BuildPassAuthProvider = ({ children }) => {
  useCookie('clerk-enabled', 'true')

  return (
    <ClerkProvider
      signInUrl={'/sign-in'}
      signInFallbackRedirectUrl={'/dashboards/overview'}
    >
      <ClerkAuthProvider>{children}</ClerkAuthProvider>
    </ClerkProvider>
  )
}

export const useAuth = () => useContext(BuildPassAuthContext)
